import { useEffect, useState } from "react";
import {
  mintCliTokenFlow,
  listCliTokens,
  revokeCliToken,
  type CliTokenView,
} from "../account-client";
import { CopyableSecret } from "./CopyableSecret";
import { PlusIcon } from "./icons";

// CLI token management (plan U5). Mint a per-box token (machine key generated
// client-side, wrapped under the account key), show the one-time credential, and
// list/revoke existing tokens. The mint form stays collapsed behind "+ Add" so
// the tab reads as a token list first.
export function CliTokensTab({ accountKey }: { accountKey: CryptoKey }) {
  const [tokens, setTokens] = useState<CliTokenView[]>([]);
  const [label, setLabel] = useState("");
  const [credential, setCredential] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Whether the (otherwise collapsed) mint form is open.
  const [adding, setAdding] = useState(false);

  async function refresh() {
    try {
      setTokens(await listCliTokens());
    } catch (e) {
      setError((e as Error).message);
    }
  }
  useEffect(() => {
    void refresh();
  }, []);

  async function mint() {
    setError(null);
    setBusy(true);
    try {
      const { credential: cred } = await mintCliTokenFlow(accountKey, label.trim() || "cli");
      setCredential(cred);
      setLabel("");
      setAdding(false);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function cancelAdd() {
    setAdding(false);
    setLabel("");
    setError(null);
  }

  async function revoke(tokenId: string) {
    setError(null);
    try {
      await revokeCliToken(tokenId);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <div className="cli-tokens">
      <p className="tab-hint">
        Create a token for a server box, then run the CLI there to paste it — it
        uploads JSONL files into your encrypted history.
      </p>
      <CopyableSecret
        value="npx @jsonl-tools/cli login"
        variant="command"
        prefix={<span className="cli-prompt">$</span>}
      />

      <div className="cli-tokens-header">
        <h3 className="cli-tokens-title">Tokens</h3>
        {!adding && (
          <button
            type="button"
            className="cli-add-button"
            onClick={() => {
              setError(null);
              setAdding(true);
            }}
          >
            <PlusIcon />
            Add
          </button>
        )}
      </div>

      {adding && (
        <div className="input-row cli-add-form">
          <input
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            type="text"
            placeholder="Label (e.g. ci-runner)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !busy) void mint();
              if (e.key === "Escape") cancelAdd();
            }}
          />
          <button onClick={mint} disabled={busy}>
            {busy ? "Creating…" : "Create token"}
          </button>
          <button type="button" className="button-ghost" onClick={cancelAdd} disabled={busy}>
            Cancel
          </button>
        </div>
      )}

      {credential && (
        <div className="recovery-code">
          <p>
            <strong>Copy this credential now</strong> — it is shown once. It
            carries a decryption key for this box; treat it like a password.
          </p>
          <CopyableSecret value={credential} />
          <button onClick={() => setCredential(null)}>Done</button>
        </div>
      )}

      {tokens.length === 0 ? (
        <p className="cli-tokens-empty">No CLI tokens yet.</p>
      ) : (
        <ul className="cli-token-list">
          {tokens.map((t) => (
            <li key={t.tokenId} className="cli-token-row">
              <div className="cli-token-info">
                <span className="cli-token-label">
                  {t.label ?? "(unlabeled)"}
                  {t.revoked && <span className="cli-token-tag">revoked</span>}
                </span>
                <span className="cli-token-meta">
                  {new Date(t.createdAt).toLocaleDateString()}
                  {t.lastUsedAt
                    ? ` · last used ${new Date(t.lastUsedAt).toLocaleDateString()}`
                    : " · never used"}
                </span>
              </div>
              {!t.revoked && (
                <button className="cli-revoke-button" onClick={() => revoke(t.tokenId)}>
                  Revoke
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {error && <div className="validation-error">{error}</div>}
    </div>
  );
}

export default CliTokensTab;
